import { collection, doc, getDocs, deleteDoc, collectionGroup, query, where } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import { getPropertiesOnce, deleteAllPropertiesForUser, clearBrokerFromAllProperties } from './dataService';
import { cleanupPropertyShares, deleteAllSharesForUser } from './shareService';
import { deleteMarketParamsForUser } from './marketParamsService';
import { removeBrokerLink, removeBrokerLinkByDocId } from './brokerLinksService';

const CLIENTS_COLLECTION = 'clients';

/**
 * Remove all broker links that reference this broker (so they disappear from "Partager avec quel courtier?").
 * 1) Tries collection group query (needs index brokerLinks.brokerUid COLLECTION_GROUP). If that fails (e.g. missing index), 
 * 2) Falls back to clients collection: get broker's clients with clientUserId and remove those investors' links.
 */
async function removeBrokerLinksReferencingUser(uid) {
  if (!uid) return;
  try {
    const colRef = collectionGroup(firestore, 'brokerLinks');
    const q = query(colRef, where('brokerUid', '==', uid));
    const snapshot = await getDocs(q);
    await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)));
    if (snapshot.size > 0) return;
  } catch (e) {
    console.warn('removeBrokerLinksReferencingUser: collection group failed, using clients fallback', e?.message);
  }
  const clientsRef = collection(firestore, CLIENTS_COLLECTION);
  const clientQ = query(clientsRef, where('uid', '==', uid));
  const clientSnap = await getDocs(clientQ);
  await Promise.all(
    clientSnap.docs.map(async (d) => {
      const data = d.data();
      const investorUid = data.clientUserId;
      if (!investorUid) return;
      try {
        await removeBrokerLink(investorUid, uid);
      } catch (e) {
        console.warn('removeBrokerLinksReferencingUser: removeBrokerLink', investorUid, e);
      }
      try {
        await removeBrokerLinkByDocId(investorUid, d.id);
      } catch (e) {
        console.warn('removeBrokerLinksReferencingUser: removeBrokerLinkByDocId', d.id, e);
      }
    })
  );
}

/**
 * Remove all client documents that reference this user as clientUserId (investor who linked).
 * So when an investor deletes their account, they disappear from every broker's client list.
 */
async function removeClientRecordsForUser(uid) {
  if (!uid) return;
  const ref = collection(firestore, CLIENTS_COLLECTION);
  const q = query(ref, where('clientUserId', '==', uid));
  const snapshot = await getDocs(q);
  await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)));
}

/**
 * Delete all Firestore data for the given user (properties, scenarios, shares,
 * sharedWithMe, brokerLinks, marketParams, user profile). Does not delete the
 * Firebase Auth account; caller must call auth.deleteUser() after this.
 * When the user is a broker, also removes their link from every investor's brokerLinks.
 * When the user is an investor (client), removes their client record from every broker's list.
 * @param {string} uid - Firebase Auth UID
 */
export async function deleteAllUserData(uid) {
  if (!uid) return;

  // 0. If this user is a broker: remove them from all properties' sharedWithBrokerUids (blue button) and from every investor's brokerLinks (disappear from modal)
  try {
    await clearBrokerFromAllProperties(uid);
  } catch (e) {
    console.warn('deleteAllUserData: clearBrokerFromAllProperties', uid, e);
  }
  try {
    await removeBrokerLinksReferencingUser(uid);
  } catch (e) {
    console.warn('deleteAllUserData: removeBrokerLinksReferencingUser', uid, e);
  }

  // 0b. If this user is an investor (clientUserId on client docs), remove those client records so broker no longer sees them
  try {
    await removeClientRecordsForUser(uid);
  } catch (e) {
    console.warn('deleteAllUserData: removeClientRecordsForUser', uid, e);
  }

  // 1. For each property: clean shares (sharedWithMe entries, share docs), then delete property + scenarios
  const properties = await getPropertiesOnce(uid);
  for (const p of properties) {
    try {
      await cleanupPropertyShares(uid, p.id);
    } catch (e) {
      console.warn('deleteAllUserData: cleanupPropertyShares', p.id, e);
    }
  }
  await deleteAllPropertiesForUser(uid);

  // 2. Delete any remaining shares owned by this user (e.g. orphan share docs)
  await deleteAllSharesForUser(uid);

  // 3. Delete user subcollections (sharedWithMe, brokerLinks)
  const sharedWithMeRef = collection(firestore, 'users', uid, 'sharedWithMe');
  const sharedWithMeSnap = await getDocs(sharedWithMeRef);
  await Promise.all(sharedWithMeSnap.docs.map((d) => deleteDoc(d.ref)));

  const brokerLinksRef = collection(firestore, 'users', uid, 'brokerLinks');
  const brokerLinksSnap = await getDocs(brokerLinksRef);
  await Promise.all(brokerLinksSnap.docs.map((d) => deleteDoc(d.ref)));

  // 4. Delete market params for this user
  await deleteMarketParamsForUser(uid);

  // 5. Delete user profile document
  const userRef = doc(firestore, 'users', uid);
  await deleteDoc(userRef);
}
